import type { ReactNode } from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { ZoomIn, ZoomOut } from "lucide-react";
import type {
  FigureSettings,
  Instance,
  LineKind,
  NumeralStyle,
} from "@/lib/element-registry";
import { lineKindOf, styleOf } from "@/lib/element-registry";
import type { MarkEdits } from "./AnnotationCard";
import {
  SEGMENT,
  SEGMENT_CHOSEN,
  SEGMENTED,
  SEGMENTED_OFF,
  STEP_BUTTON,
  STEP_VALUE,
  STEPPER,
  TOOLBAR_DIVIDER,
  TOOLBAR_GROUP,
  TOOLBAR_STRIP,
} from "./panel-styles";
import { ZOOM_MAX, percent } from "./figure-zoom";
import type { ZoomControl } from "./figure-zoom";

/**
 * A toolbar glyph: the thing the button sets, drawn at the size of an icon.
 *
 * Drawn rather than named because no icon set carries a bow and an S that read as two
 * versions of one line. Sharing the box and the stroke weight is what makes the four line
 * shapes comparable at a glance, which is the whole job of a segmented control.
 */
function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** Each leader line as its own picture: a bare numeral, a rule, a bow, an S. */
const LINE_GLYPH: Record<LineKind, ReactNode> = {
  none: (
    <Glyph>
      <circle cx="8" cy="8" r="5.4" strokeDasharray="2 2" />
      <line x1="4.4" y1="11.6" x2="11.6" y2="4.4" />
    </Glyph>
  ),
  straight: (
    <Glyph>
      <line x1="3" y1="12.6" x2="13" y2="3.4" />
    </Glyph>
  ),
  curved: (
    <Glyph>
      <path d="M3 12.6 Q4.8 5 13 3.4" />
    </Glyph>
  ),
  "s-curve": (
    <Glyph>
      <path d="M3 13 C10.7 11.7 5.3 4.3 13 3" />
    </Glyph>
  ),
};

/** A line ending in a head, and the same line ending in a bare tick. */
const HEAD_GLYPH: Record<"on" | "off", ReactNode> = {
  on: (
    <Glyph>
      <line x1="3" y1="12.6" x2="11.4" y2="4.8" />
      <path d="M8.1 4.1 L12.5 3.5 L11.9 7.9" strokeLinejoin="round" />
    </Glyph>
  ),
  off: (
    <Glyph>
      <line x1="3" y1="12.6" x2="12.2" y2="4.2" />
      <line x1="11.2" y1="2.9" x2="14.1" y2="5.3" />
    </Glyph>
  ),
};

/** A numeral in its ring, and the same numeral standing on the drawing bare. */
const NUMERAL_GLYPH: Record<NumeralStyle, ReactNode> = {
  circled: (
    <Glyph>
      <circle cx="8" cy="8" r="5.6" />
      <text
        x="8"
        y="10.8"
        textAnchor="middle"
        fontSize="7.5"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
        className="font-mono"
      >
        N
      </text>
    </Glyph>
  ),
  plain: (
    <Glyph>
      <text
        x="8"
        y="12"
        textAnchor="middle"
        fontSize="11.5"
        fontWeight="700"
        fill="currentColor"
        stroke="none"
        className="font-mono"
      >
        N
      </text>
    </Glyph>
  ),
};

/** One choice in a segmented control, named for the ear and drawn for the eye. */
function Segment({
  label,
  hint,
  glyph,
  chosen,
  disabled,
  onChoose,
}: {
  label: string;
  hint: string;
  glyph: ReactNode;
  chosen: boolean;
  disabled: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      // Held as well as the label, because the glyph says which line this is but not what
      // dragging it afterwards will do, and that is the part an attorney cannot guess.
      title={hint}
      aria-pressed={chosen}
      disabled={disabled}
      onClick={onChoose}
      className={`${SEGMENT} ${chosen ? SEGMENT_CHOSEN : ""}`}
    >
      {glyph}
    </button>
  );
}

/** A labelled run of choices, greyed as one when the choice cannot be made yet. */
function ToolGroup({
  label,
  off,
  children,
}: {
  label: string;
  off: boolean;
  children: ReactNode;
}) {
  return (
    <span role="group" aria-label={label} className={TOOLBAR_GROUP}>
      <MicroLabel size="sm" className="text-center leading-none">
        {label}
      </MicroLabel>
      <span className={`${SEGMENTED} ${off ? SEGMENTED_OFF : ""}`}>
        {children}
      </span>
    </span>
  );
}

/** The four leader lines, in the order a line gains detail. */
const LINE_CHOICES: { kind: LineKind; label: string; hint: string }[] = [
  {
    kind: "none",
    label: "No line",
    hint: "No line, the numeral sits on the drawing on its own",
  },
  { kind: "straight", label: "Straight line", hint: "Straight line" },
  { kind: "curved", label: "Curved line", hint: "Curved line" },
  {
    kind: "s-curve",
    label: "S curve",
    hint: "S curve, shaped by dragging either of its two anchors",
  },
];

/** The four leader lines, each offered as the picture of the line it draws. */
function LineGroup({
  instance,
  edits,
}: {
  instance: Instance | undefined;
  edits: MarkEdits;
}) {
  const kind = instance === undefined ? undefined : lineKindOf(instance);
  return (
    <ToolGroup label="Line" off={instance === undefined}>
      {LINE_CHOICES.map((choice) => (
        <Segment
          key={choice.kind}
          label={choice.label}
          hint={choice.hint}
          glyph={LINE_GLYPH[choice.kind]}
          chosen={kind === choice.kind}
          disabled={instance === undefined}
          onChoose={() => {
            if (instance !== undefined) edits.line(instance.id, choice.kind);
          }}
        />
      ))}
    </ToolGroup>
  );
}

/** Whether the line ends in a head, which it can only do once there is a line. */
function HeadGroup({
  instance,
  edits,
}: {
  instance: Instance | undefined;
  edits: MarkEdits;
}) {
  const line = instance?.connector ?? undefined;
  const headless = instance === undefined || line === undefined;
  // Said whenever there is no line to carry a head, selected mark or not. A disabled button
  // advertising what it would do reads as one that is broken.
  const hint = (own: string) =>
    line === undefined ? "Choose a line first" : own;
  const choose = (on: boolean) => () => {
    if (instance !== undefined) edits.arrowhead(instance.id, on);
  };

  return (
    <ToolGroup label="Arrowhead" off={headless}>
      <Segment
        label="With arrowhead"
        hint={hint("Line ends in an arrowhead")}
        glyph={HEAD_GLYPH.on}
        chosen={line?.arrowhead === true}
        disabled={headless}
        onChoose={choose(true)}
      />
      <Segment
        label="No arrowhead"
        hint={hint("Plain line, no arrowhead")}
        glyph={HEAD_GLYPH.off}
        chosen={line?.arrowhead === false}
        disabled={headless}
        onChoose={choose(false)}
      />
    </ToolGroup>
  );
}

/**
 * How this one numeral is drawn, against whatever the set is drawn under.
 *
 * The set's own style, in Figure Settings, says what a numeral placed from now on is born
 * as. This is how one of them is made to differ.
 */
function NumeralGroup({
  instance,
  settings,
  edits,
}: {
  instance: Instance | undefined;
  settings: FigureSettings;
  edits: MarkEdits;
}) {
  const drawn =
    instance === undefined ? undefined : styleOf(instance, settings);
  const choose = (style: NumeralStyle) => () => {
    if (instance !== undefined) edits.numeralStyle(instance.id, style);
  };

  return (
    <ToolGroup label="Numeral" off={instance === undefined}>
      <Segment
        label="Circled numeral"
        hint="Circled numeral"
        glyph={NUMERAL_GLYPH.circled}
        chosen={drawn === "circled"}
        disabled={instance === undefined}
        onChoose={choose("circled")}
      />
      <Segment
        label="Plain numeral"
        hint="Plain numeral, no outline"
        glyph={NUMERAL_GLYPH.plain}
        chosen={drawn === "plain"}
        disabled={instance === undefined}
        onChoose={choose("plain")}
      />
    </ToolGroup>
  );
}

/**
 * How close the drawing is being looked at, and the two presses that change it.
 *
 * The last group on the strip, and the only one that acts on the view rather than on the
 * mark that is selected. That is why it is never dimmed with the rest: nothing about it is
 * waiting on a selection, and greying it beside the groups that are would say it is.
 *
 * The way back down is the steps themselves. A Fit control would be a third way to say
 * what two presses and every exit path already say.
 */
function ZoomGroup({ zoom }: { zoom: ZoomControl }) {
  return (
    <span
      role="group"
      aria-label="Zoom"
      data-testid="define-zoom"
      className={TOOLBAR_GROUP}
    >
      <MicroLabel size="sm" className="text-center leading-none">
        Zoom
      </MicroLabel>
      <span className={STEPPER}>
        <button
          type="button"
          aria-label="Zoom out"
          title="Zoom out"
          disabled={zoom.level <= zoom.floor}
          onClick={zoom.zoomOut}
          className={STEP_BUTTON}
        >
          <ZoomOut className="size-3.5" aria-hidden />
        </button>
        {/* Read rather than pressed, so it is not a stop in the tab ring. */}
        <span data-testid="define-zoom-level" className={STEP_VALUE}>
          {percent(zoom.level)}
        </span>
        <button
          type="button"
          aria-label="Zoom in"
          title="Zoom in (up to 200%)"
          disabled={zoom.level >= ZOOM_MAX}
          onClick={zoom.zoomIn}
          className={STEP_BUTTON}
        >
          <ZoomIn className="size-3.5" aria-hidden />
        </button>
      </span>
    </span>
  );
}

/**
 * What the strip says about itself, when a group in it cannot be used yet.
 *
 * Only the head has anything to say. A strip with no mark chosen is quiet throughout and
 * reads as such, but a lineless mark leaves one group dark among live ones, which needs
 * accounting for.
 */
function asideFor(instance: Instance | undefined): string | undefined {
  if (instance !== undefined && instance.connector == null)
    return "Give it a line to put an arrowhead on it.";
  return undefined;
}

/**
 * The controls that shape a leader line, acting on the one mark that is selected.
 *
 * Every button states an outcome rather than asking for a change, which is what makes them
 * safe to press twice: the rules behind them write a value, so pressing a control the mark
 * already satisfies costs nothing. That is also why the line group stays live on a mark with
 * no line, since picking a shape is how a line comes back. The head has nowhere to sit until
 * one does, so that group waits.
 */
export function AnnotationToolbar({
  instance,
  settings,
  edits,
  zoom,
}: {
  instance: Instance | undefined;
  settings: FigureSettings;
  edits: MarkEdits;
  /**
   * How close the drawing is, while it is filling the screen.
   *
   * Absent at panel size, where there is nothing to zoom: the drawing takes the column it
   * is in and the page scrolls. The group and the divider that introduces it go together,
   * so the strip never ends in a rule with nothing after it.
   */
  zoom?: ZoomControl;
}) {
  const aside = asideFor(instance);
  return (
    <div data-testid="define-annotation-toolbar" className={TOOLBAR_STRIP}>
      <LineGroup instance={instance} edits={edits} />
      <HeadGroup instance={instance} edits={edits} />
      <NumeralGroup instance={instance} settings={settings} edits={edits} />

      {zoom !== undefined && (
        <>
          <span
            aria-hidden
            data-testid="define-zoom-divider"
            className={TOOLBAR_DIVIDER}
          />
          <ZoomGroup zoom={zoom} />
        </>
      )}

      {/* Said in the strip rather than only in the disabled buttons' tooltips. A disabled
          button cannot take keyboard focus, so its title reaches nobody without a pointer,
          and it would be the only thing saying why a whole group has gone quiet. */}
      {aside !== undefined && (
        <span className="flex items-center self-end pb-1.5 text-[11px] text-muted-foreground">
          {aside}
        </span>
      )}
    </div>
  );
}
